import { ReactNode } from 'react';
import { UseMutationResult } from '@tanstack/react-query';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';

export type ErrorResponse = {
  status: number;
  error: {
    isSuccess?: boolean;
    errors?: Record<string, string>;
    message?: string;
  };
};

export type ToastOptions = {
  title?: string;
  description?: ReactNode;
  variant?: 'default' | 'destructive' | 'success';
};

type MutationFn<TData = unknown> = (...args: any[]) => Promise<TData>;

export type GlobalMutationResult<TData = unknown> = UseMutationResult<
  TData,
  ErrorResponse,
  Parameters<MutationFn<TData>>[0]
>;

export const useCustomToast = () => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const showSuccessToast = (options: ToastOptions = {}) => {
    toast({
      title: options.title ?? t('SUCCESS', 'Success'),
      description: options.description,
      variant: 'success',
    });
  };

  const showErrorToast = (error: any, options: ToastOptions = {}) => {
    let messages: string[] = [];

    const actualError = error?.error ? error.error : error;

    if (actualError?.errors && typeof actualError.errors === 'object') {
      messages = Object.values(actualError.errors).filter(Boolean) as string[];
    } else if (actualError?.message) {
      messages = [actualError.message];
    } else if (typeof actualError === 'string') {
      messages = [actualError];
    } else if (error?.message) {
      messages = [error.message];
    } else {
      messages = [t('AN_UNEXPECTED_ERROR_OCCURRED', 'An unexpected error occurred. Please try again later.')];
    }

    toast({
      variant: options.variant ?? 'destructive',
      title: options.title ?? t('ERROR', 'Error'),
      description: (
        <div className="flex flex-col gap-1">
          {messages.map((message, i) => (
            <div key={i}>{message}</div>
          ))}
        </div>
      ),
    });
  };

  return {
    showSuccessToast,
    showErrorToast,
  };
};
